import {
  db,
  type LocalSale,
  type LocalSaleLine,
  type LocalTender,
  type LocalDiscount,
  type LocalShift,
  type LocalCashMovement,
  type LocalAuditEntry,
  type LocalVoid,
  type LocalRefund,
  type LocalRefundLine,
  type OutboxEnvelope,
} from "./db";
import type { BusinessDate, IsoTimestamp } from "../../domain/time";

/**
 * Local persistence.
 *
 * The one function that matters here is `commitAtomically`. Everything else is
 * ordinary reads.
 */

export interface AtomicCommit {
  /** The financial records being created. All or none. */
  sale?: LocalSale;
  saleLines?: LocalSaleLine[];
  tenders?: LocalTender[];
  discounts?: LocalDiscount[];
  shift?: LocalShift;
  cashMovement?: LocalCashMovement;
  auditEntry?: LocalAuditEntry;
  voidRecord?: LocalVoid;
  refund?: LocalRefund;
  refundLines?: LocalRefundLine[];
  /** The envelope that will carry this to the server. */
  envelope: OutboxEnvelope;
  /** Series whose counter advances as part of this commit, if any. */
  advanceCounter?: { series: string; toSequence: number };
}

/**
 * Commit a set of records and its outbox envelope in ONE IndexedDB transaction.
 *
 * This single function is where Principle I is either honoured or broken:
 *
 *   * The UI confirms from this commit, never from a server response, so the
 *     till keeps working with no network (FR-043).
 *   * The document-number counter advances INSIDE the same transaction as the
 *     sale, so a crash between allocating a number and writing the sale is not
 *     a state that can exist — which is what makes numbering gap-free (FR-019)
 *     rather than merely usually-gap-free.
 *   * The outbox envelope is written with the records, so a sale can never
 *     exist locally without something scheduled to carry it upward.
 *
 * If any part throws, Dexie rolls the whole thing back and the sale never
 * happened. There is no partial sale.
 */
export async function commitAtomically(commit: AtomicCommit): Promise<void> {
  await db.transaction(
    "rw",
    [
      db.sales,
      db.saleLines,
      db.tenders,
      db.discounts,
      db.shifts,
      db.cashMovements,
      db.auditEntries,
      db.voids,
      db.refunds,
      db.refundLines,
      db.outbox,
      db.counters,
    ],
    async () => {
      if (commit.advanceCounter) {
        const { series, toSequence } = commit.advanceCounter;
        const current = await db.counters.get(series);

        // The counter may only ever move forward by exactly one. Anything else
        // means two writers raced, and continuing would risk issuing a
        // duplicate document number.
        if (current && current.nextSequence !== toSequence) {
          throw new Error(
            `Counter race on series ${series}: expected ${toSequence}, found ${current.nextSequence}`,
          );
        }
        await db.counters.put({ series, nextSequence: toSequence + 1 });
      }

      if (commit.sale) await db.sales.add(commit.sale);
      if (commit.saleLines?.length) await db.saleLines.bulkAdd(commit.saleLines);
      if (commit.tenders?.length) await db.tenders.bulkAdd(commit.tenders);
      if (commit.discounts?.length) await db.discounts.bulkAdd(commit.discounts);
      if (commit.shift) await db.shifts.put(commit.shift);
      if (commit.cashMovement) await db.cashMovements.add(commit.cashMovement);
      if (commit.auditEntry) await db.auditEntries.add(commit.auditEntry);
      if (commit.voidRecord) await db.voids.add(commit.voidRecord);
      if (commit.refund) await db.refunds.add(commit.refund);
      if (commit.refundLines?.length) await db.refundLines.bulkAdd(commit.refundLines);

      await db.outbox.add(commit.envelope);
    },
  );
}

// ---------------------------------------------------------------------------
// Counters
// ---------------------------------------------------------------------------

/** The sequence this series would use next. Does NOT reserve it. */
export async function peekNextSequence(series: string): Promise<number> {
  const row = await db.counters.get(series);
  return row?.nextSequence ?? 1;
}

export async function initialiseCounter(series: string, nextSequence = 1): Promise<void> {
  const existing = await db.counters.get(series);
  if (!existing) await db.counters.put({ series, nextSequence });
}

// ---------------------------------------------------------------------------
// Sales
// ---------------------------------------------------------------------------

export async function getSale(saleId: string): Promise<LocalSale | undefined> {
  return db.sales.get(saleId);
}

export async function getSaleByDocumentNumber(
  documentNumber: string,
): Promise<LocalSale | undefined> {
  return db.sales.where("documentNumber").equals(documentNumber).first();
}

export async function getSaleLines(saleId: string): Promise<LocalSaleLine[]> {
  return db.saleLines.where("saleId").equals(saleId).sortBy("lineNumber");
}

export async function getSaleTenders(saleId: string): Promise<LocalTender[]> {
  return db.tenders.where("saleId").equals(saleId).toArray();
}

export async function getSaleDiscounts(saleId: string): Promise<LocalDiscount[]> {
  return db.discounts.where("saleId").equals(saleId).toArray();
}

/**
 * Sales rung on one business day, newest first.
 *
 * Scoped by `businessDate` — the trading-day field already stored on every
 * sale, indexed, and computed in the business timezone rather than UTC — so
 * the browsing list matches what a person means by "today" even for a café
 * that closes after midnight local time.
 */
export async function getSalesForBusinessDate(date: BusinessDate): Promise<LocalSale[]> {
  const rows = await db.sales.where("businessDate").equals(date).toArray();
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function getSalesForShift(shiftId: string): Promise<LocalSale[]> {
  return db.sales.where("shiftId").equals(shiftId).toArray();
}

/**
 * Tenders for a set of sales, in one indexed read.
 *
 * The browsing list needs the payment method on every row. Reading them one
 * sale at a time is a query per row on a store that already has an index on
 * `saleId` — `anyOf` walks it once instead.
 */
export async function getTendersForSales(
  saleIds: string[],
): Promise<Map<string, LocalTender[]>> {
  const byId = new Map<string, LocalTender[]>();
  if (saleIds.length === 0) return byId;

  const rows = await db.tenders.where("saleId").anyOf(saleIds).toArray();
  for (const tender of rows) {
    const existing = byId.get(tender.saleId);
    if (existing) existing.push(tender);
    else byId.set(tender.saleId, [tender]);
  }
  return byId;
}

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

export async function getOpenShift(terminalId: string): Promise<LocalShift | undefined> {
  const open = await db.shifts.where("status").equals("open").toArray();
  return open.find((s) => s.terminalId === terminalId);
}

/**
 * Every shift that traded on one business day, oldest first.
 *
 * A terminal is handed over mid-day: one person opens and closes the till,
 * the next opens a fresh one. Both shifts share a business date, so a
 * date-scoped list of sales mixes two people's takings together. This is
 * what lets the browsing list scope to a single shift instead.
 */
export async function getShiftsForBusinessDate(date: BusinessDate): Promise<LocalShift[]> {
  const rows = await db.shifts.where("businessDate").equals(date).toArray();
  return rows.sort((a, b) => a.openedAt.localeCompare(b.openedAt));
}

export async function getCashMovements(shiftId: string): Promise<LocalCashMovement[]> {
  return db.cashMovements.where("shiftId").equals(shiftId).toArray();
}

export async function getAuditEntriesForShift(
  shiftId: string,
  action?: string,
): Promise<LocalAuditEntry[]> {
  const rows = await db.auditEntries.where("shiftId").equals(shiftId).toArray();
  return action ? rows.filter((r) => r.action === action) : rows;
}

// ---------------------------------------------------------------------------
// Outbox
// ---------------------------------------------------------------------------

/**
 * Oldest first. op_id is UUIDv7, so id order is creation order.
 *
 * `ignoreBackoff` is used on an explicit reconnect. Backoff computed during an
 * outage is stale information the instant the connection demonstrably returns —
 * making staff wait out a delay that was calculated while the network was down
 * would leave the back office showing quietly wrong figures for no reason.
 */
export async function getPendingEnvelopes(
  limit: number,
  ignoreBackoff = false,
): Promise<OutboxEnvelope[]> {
  const now = new Date().toISOString();
  const pending = await db.outbox.where("state").equals("pending").sortBy("opId");
  const ready = ignoreBackoff
    ? pending
    : pending.filter((e) => !e.nextAttemptAt || e.nextAttemptAt <= now);
  return ready.slice(0, limit);
}

/**
 * Envelopes left in flight by an interrupted request.
 *
 * A drain that dies mid-request (tab closed, connection dropped after the
 * server committed) leaves these stranded. They are safe to re-send because the
 * server dedupes by op_id — the risk of NOT re-sending them is a sale that
 * never reaches the books.
 */
export async function recoverStranded(): Promise<number> {
  return db.outbox.where("state").equals("in_flight").modify({ state: "pending" });
}

export async function countPending(): Promise<number> {
  return db.outbox.where("state").anyOf("pending", "in_flight").count();
}

export async function markInFlight(opIds: string[]): Promise<void> {
  await db.outbox.where("opId").anyOf(opIds).modify({ state: "in_flight" });
}

/**
 * Acknowledge an envelope. `duplicate` from the server is SUCCESS, not an
 * error — it means the server already has this exact operation.
 */
export async function markAcked(opId: string, syncedAt: IsoTimestamp): Promise<void> {
  await db.transaction("rw", [db.outbox, db.sales], async () => {
    const envelope = await db.outbox.get(opId);
    await db.outbox.delete(opId);

    // Clear the pending indicator on the sale this envelope carried.
    const saleId = (envelope?.payload as { saleId?: string } | undefined)?.saleId;
    if (saleId) {
      const sale = await db.sales.get(saleId);
      if (sale) await db.sales.update(saleId, { syncedAt });
    }
  });
}

/**
 * A permanently invalid envelope. Retained and surfaced to staff — never
 * silently dropped (FR-049). A rejected sale is a real event that happened in
 * the café, and someone has to reconcile it.
 */
export async function markRejected(opId: string, error: string): Promise<void> {
  await db.outbox.update(opId, { state: "rejected", lastError: error });
}

export async function scheduleRetry(opId: string, attempts: number): Promise<void> {
  // Exponential backoff, capped so a terminal that has been offline for hours
  // still retries promptly once the network returns.
  const delayMs = Math.min(30_000, 1_000 * 2 ** Math.min(attempts, 5));
  await db.outbox.update(opId, {
    state: "pending",
    attempts,
    nextAttemptAt: new Date(Date.now() + delayMs).toISOString(),
  });
}

export async function getRejectedEnvelopes(): Promise<OutboxEnvelope[]> {
  return db.outbox.where("state").equals("rejected").toArray();
}

// ---------------------------------------------------------------------------
// Corrections
// ---------------------------------------------------------------------------

export async function getVoidForSale(saleId: string): Promise<LocalVoid | undefined> {
  return db.voids.where("saleId").equals(saleId).first();
}

export async function getRefundsForSale(saleId: string): Promise<LocalRefund[]> {
  return db.refunds.where("saleId").equals(saleId).toArray();
}

export async function getRefundLines(refundId: string): Promise<LocalRefundLine[]> {
  return db.refundLines.where("refundId").equals(refundId).toArray();
}

/**
 * Units already returned, per sale line.
 *
 * Read across EVERY refund on the sale, not just the last one — a line refunded
 * one unit at a time across three visits has to know that all three are gone.
 */
export async function getRefundedQuantities(saleId: string): Promise<Map<string, number>> {
  const refunds = await getRefundsForSale(saleId);
  const quantities = new Map<string, number>();

  for (const refund of refunds) {
    for (const line of await getRefundLines(refund.id)) {
      quantities.set(line.saleLineId, (quantities.get(line.saleLineId) ?? 0) + line.quantity);
    }
  }

  return quantities;
}

export async function getVoidsForShift(shiftId: string): Promise<LocalVoid[]> {
  return db.voids.where("shiftId").equals(shiftId).toArray();
}

export async function getRefundsForShift(shiftId: string): Promise<LocalRefund[]> {
  return db.refunds.where("shiftId").equals(shiftId).toArray();
}
